import { useMemo, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../core/hooks/useTranslation'
import { useAuthStore } from '../core/stores/authStore'
import { canAccessRoute } from '../core/config/route-permissions'
import UserHeader from '../components/layout/UserHeader'
import UserSidebar, { NavigationItem } from '../components/layout/UserSidebar'
import {
  HomeIcon,
} from '@heroicons/react/24/outline'
import { AppRoute } from '../core/config/app-routes'

interface SystemAdminLayoutProps {
  children: React.ReactNode
}

export default function SystemAdminLayout({ children }: SystemAdminLayoutProps) {
  const location = useLocation()
  const { t, isRTL } = useTranslation()
  const { user } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  
  // Close sidebar when route changes on mobile
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])
  
  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [sidebarOpen])

  const allNavigationItems: NavigationItem[] = useMemo(() => {
    const items: NavigationItem[] = [
      {
        name: t('dashboard'),
        href: AppRoute.SYSTEM_ADMIN_DASHBOARD_ROUTE,
        icon: HomeIcon
      }
    ]
    
    return items
  }, [t])

  // Filter navigation items based on user permissions
  const navigation = useMemo(() => {
    if (!user) return []
    
    return allNavigationItems.filter(item => 
      canAccessRoute(item.href, user.userRole)
    )
  }, [user, location.pathname, t])

  const activePage = navigation.find((item) => location.pathname.startsWith(item.href))

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-stone-50 to-stone-100">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[45] md:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Header */}
      <UserHeader 
        activePageName={activePage?.name}
        sidebarWidth={256}
        subtitle="adminPanel"
        showNotifications={false}
        onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        sidebarOpen={sidebarOpen}
      />

      <div className="flex">
        {/* Sidebar */}
        <UserSidebar
          navigationItems={navigation}
          variant="dark"
          showHelpSection={false}
          logoHref={AppRoute.SYSTEM_ADMIN_DASHBOARD_ROUTE}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <main className={`
          flex-1 p-4 sm:p-6 overflow-auto transition-all duration-300
          ${isRTL 
            ? 'md:mr-64' 
            : 'md:ml-64'
          }
        `}>
          <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}